import {useSelectedTimings} from "context/TimingContext";
import {offlineOption, onlineOption, onlineTypeOptions,} from "conts/options";
import {defaultTypeClasses} from "conts";
import {getNumberOfStudentsOptions, useTeacherSlots,} from "hooks/useTeacherSlots";
import moment from "moment";
import {useEffect, useState} from "react";
import styled from "styled-components";
import {ActionButton} from "ui/base/Button/ButtonStyled";
import {DateCard} from "ui/base/dateCard/DateCard";
import {DatePicker} from "ui/base/datePicker/DatePicker";
import { LoaderComponent } from "ui/base/loading/LoaderComponent";
import {SelectButton} from "ui/base/selectInput/SelectButton";
import {CheckMarkIcon} from "ui/icons/CheckMark";
import {defaultThemeColors, fonts, mediaSizes} from "ui/theme";
import {scrollbarStyles} from "ui/base/scrollbarStyle/scrollbarStyle";
import {useFilters} from "context/FilterContext";
import {getClassType} from "utils";
import {useLocation} from "react-router-dom";

export const DatesList = ({classDetail}) => {
    const {filters, setFilters} = useFilters();
    const location = useLocation();
    const {typeOfClass: rTypeOfClass} = location.state || {};

    const teacherInfo = classDetail?.teacher;
    const onlineType = classDetail?.teacher.online_type;
    const hasOnlineClasses = !!teacherInfo?.online_slots?.length
    const defaultOnlineType = getClassType(onlineType, hasOnlineClasses);
    const isOnline = (filters?.online_type?.value || defaultOnlineType) === "online" && hasOnlineClasses;

    const [selectedType, setSelectedType] = useState(null);

    const {
        typeOfClass: savedTypeOfClass,
        selectedTimings,
        handleAddSelectedTiming,
        handleRemoveSelectedTiming,
        handleResetSelectedTiming,
        handleChangeTypeOfClass,
        handleChangeNumberOfStudents,
    } = useSelectedTimings();

    // TODO: make one source of truth for numberOfStudents and typeOfClass

    const {
        selectedDate,
        setSelectedDate,
        typeOfClass,
        setTypeOfClass,
        numberOfStudents,
        setNumberOfStudents,
        availableTimings,
        slotsFilterDate,
        availableGroupTypes,
        handleMonthChange,
        isLoading,
    } = useTeacherSlots(
        {
            teacherInfo,
            online_type: isOnline ? "online" : 'offline'
        });

    const updateNumberOfStudents = (value) => {
        setNumberOfStudents(value);
        handleChangeNumberOfStudents(value);
    };

    useEffect(() => {
        setTypeOfClass(rTypeOfClass)
    }, [rTypeOfClass])

    useEffect(() => {
        setTypeOfClass(defaultTypeClasses.Private);
    }, [teacherInfo?.id]);

    const classOptions =
        onlineType === "online"
            ? onlineOption
            : onlineType === "offline"
                ? offlineOption
                : onlineTypeOptions;

    useEffect(() => {
        if (savedTypeOfClass?.value === typeOfClass?.value) return;

        handleChangeTypeOfClass(typeOfClass);
    }, [typeOfClass]);

    const handleGetIsSelectedDate = ({id, date}) =>
        selectedTimings?.some((value) => value.id === id && value.date === date);

    const getSelectedType = () => {
        if (selectedTimings.length === 0) {
            setSelectedType(null);
            return null;
        }

        const firstType = selectedTimings[0]?.type || null;
        setSelectedType(firstType);
    };

    const renderAvailabilityInfo = (timing) => {
        if (timing.remainingSlots) {
            return (
                <CaptionSpot>
                    {timing.remainingSlots}{" "}
                    {timing.remainingSlots === 1 ? "spot" : "spots"} remaining
                </CaptionSpot>
            );
        }
        return null;
    };

    useEffect(() => {
        getSelectedType();
    }, [selectedTimings]);

    return (
        <CardContainer>
            <FilterSection>
                <DatePickerWrapper>
                    <DatePicker
                        minDate={moment().toDate()}
                        date={selectedDate}
                        setDate={setSelectedDate}
                        filterDate={slotsFilterDate}
                        dayClassName={(date) => {
                            return slotsFilterDate(date) ? "" : "unavailable-date";
                        }}
                        labelSize={"small"}
                        onMonthChange={handleMonthChange}
                    />
                </DatePickerWrapper>
                <SelectButton
                    options={classOptions}
                    value={filters?.online_type?.value ? filters.online_type : classOptions[0]}
                    onChange={(option) => {
                        handleResetSelectedTiming();
                        setFilters({online_type: option});
                    }}
                    showIndicator={classOptions?.length > 1}
                    bgColor="white"
                    valueColor="txt"
                    labelSize={"small"}
                    placeholderColor="txt"
                    chevronColor="#191D3A"
                    height="43px"
                    textAlign="center"
                />
                <SelectButton
                    options={getNumberOfStudentsOptions(teacherInfo, defaultTypeClasses.Private)}
                    value={numberOfStudents}
                    onChange={updateNumberOfStudents}
                    showIndicator={getNumberOfStudentsOptions(teacherInfo, defaultTypeClasses.Private)?.length > 1}
                    placeholder="Number of Students"
                    bgColor="white"
                    labelSize={"small"}
                    valueColor="txt"
                    placeholderColor="txt"
                    chevronColor="#191D3A"
                    height="43px"
                    textAlign="center"
                />
            </FilterSection>
            <HeaderSection>
                <Title>Upcoming Classes</Title>
            </HeaderSection>
            <div>
                <ScheduleHeader>
                    <HeaderLabel>TYPE</HeaderLabel>
                    <HeaderLabel>DATE</HeaderLabel>
                    <HeaderLabel>TIME</HeaderLabel>
                    <HeaderLabel>COST</HeaderLabel>
                    <HeaderLabel></HeaderLabel>
                </ScheduleHeader>
                <ScheduleContainer>
                    {isLoading ? (
                        <LoaderComponent />
                    ) : null}

                    {availableTimings.length === 0 && !isLoading ? (
                        <div className="no-classes-message">
                            No available classes found for the selected date and class type.
                        </div>
                    ) : null}
                    {availableTimings?.map((timing) => {
                        const isSelected = handleGetIsSelectedDate({
                            id: timing.id,
                            date: timing.date,
                        });

                        return (
                            <DateCard
                                key={timing.id}
                                date={timing}
                                hideTitle
                                disablePadding
                                action={
                                    <ActionContainer hide={!timing.remainingSlots}>
                                        <ActionButton
                                            variant={isSelected ? "primary" : "outline"}
                                            onClick={() =>
                                                isSelected
                                                    ? handleRemoveSelectedTiming({
                                                        id: timing.id,
                                                        date: timing.date,
                                                    })
                                                    : handleAddSelectedTiming(timing)
                                            }
                                        >
                                            {
                                                isSelected
                                                ? <CheckMarkIcon width="15" height='15'/>
                                                : <>
                                                    <p>Choose</p>
                                                    <CheckMarkIcon className='hidden-check' width="15" height='15' stroke='transparent' />
                                                    </>
                                            }
                                        </ActionButton>

                                        {renderAvailabilityInfo(timing)}
                                    </ActionContainer>
                                }
                            />
                        );
                    })}
                </ScheduleContainer>
            </div>
        </CardContainer>
    );
};

const CardContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    min-height: 0;
    border-radius: 5px;
    max-width: 640px;
    order: 2;
    margin-inline: auto;
    font-family: Montserrat;

    @media (width >= ${mediaSizes.md}) {
        order: 1;
        margin-inline: unset;
        padding: 1rem;
    }
`;

const HeaderSection = styled.div`
    display: grid;
    gap: 1rem;
`;

const Title = styled.p`
    color: ${defaultThemeColors.txt};
    font-size: ${fonts.sizes.regular};
    font-weight: 600;
`;

const FilterSection = styled.div`
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 1fr 1fr;
    @media (width >= ${mediaSizes.md}) {
        gap: 1rem;
    }
`;

const DatePickerWrapper = styled.div`
    grid-column: 1 / -1;
`;

const ScheduleHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid ${defaultThemeColors.lightBlue};
`;

const HeaderLabel = styled.span`
    color: ${defaultThemeColors.placeholder};
    font-size: ${fonts.sizes.extraSmall};
    font-weight: 600;
    text-align: left;
`;

const ScheduleContainer = styled.div`
    display: grid;
    overflow: auto;
    min-height: 0;
    ${scrollbarStyles}
`;

const ActionContainer = styled.div`
    height: auto;
    &:hover {
        padding-bottom: ${({hide}) => (hide ? "0rem" : "1rem")};
    }
    
    & p{

        display: none;
        @media (width >= ${mediaSizes.md}) {
            display: flex;
        }
    }
    
    & .hidden-check {
        @media (width >= ${mediaSizes.md}) {
        display: none;
    }
    }
`;

const CaptionSpot = styled.p`
    position: absolute;

    color: ${fonts.sizes.blue};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    text-align: center;
    margin-top: 4px;
    height: 0px;
    opacity: 0;
    overflow: visible;
    z-index: 50;
    transition: opacity 0.2s ease;

    ${ActionContainer}:hover & {
        opacity: 1;
        height: auto;

    }
`;
